import { ApiError, NetworkError } from "./errors";

class ApiClient {
  private csrf = "";
  private fetchImpl: typeof fetch = (...a) => fetch(...a);

  setCSRF(t: string) {
    this.csrf = t;
  }
  getCSRF(): string {
    return this.csrf;
  }
  setFetch(f: typeof fetch) {
    this.fetchImpl = f;
  }

  async request<T = unknown>(
    method: string,
    path: string,
    body?: unknown,
    etag?: string,
  ): Promise<{ data: T; etag: string | null }> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (method !== "GET" && method !== "HEAD") {
      headers["X-CSRF-Token"] = this.csrf;
    }
    if (etag) {
      headers["If-Match"] = `"${etag}"`;
    }
    // `fetch` rejects only when the request never completed — offline, a
    // dropped connection, DNS, CORS. An HTTP error is a *resolved* promise and
    // goes down the ApiError path below, so this catch is the transport and
    // nothing else. Without it the failure escapes as a bare TypeError and
    // every `error instanceof ApiError ? … : null` in the panel loses it.
    let resp: Response;
    try {
      resp = await this.fetchImpl(path, {
        method,
        credentials: "include",
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (cause) {
      throw new NetworkError(cause);
    }
    const responseEtag = resp.headers.get("ETag");
    if (resp.status === 204) {
      return { data: undefined as T, etag: responseEtag };
    }
    const contentType = resp.headers.get("Content-Type") || "";
    const isJSON = contentType.startsWith("application/json");
    const payload = isJSON ? await resp.json() : null;
    if (!resp.ok) {
      const code = payload?.error?.code ?? `http_${resp.status}`;
      // Keep error.message: one code covers many causes on the backend, so
      // dropping it makes those failures indistinguishable in the UI.
      throw new ApiError(
        code,
        resp.status,
        payload?.error?.details,
        payload?.error?.message,
      );
    }
    return { data: payload as T, etag: responseEtag };
  }

  get<T>(path: string) {
    return this.request<T>("GET", path);
  }
  post<T>(path: string, body?: unknown) {
    return this.request<T>("POST", path, body);
  }
  patch<T>(path: string, body?: unknown, etag?: string) {
    return this.request<T>("PATCH", path, body, etag);
  }
  delete<T>(path: string, etag?: string) {
    return this.request<T>("DELETE", path, undefined, etag);
  }
}

export const apiClient = new ApiClient();
